import React, { useState, useEffect } from "react";
import {
  Download,
  Description,
  CheckCircle,
  Refresh
} from "@mui/icons-material";
import {
  Button,
  Chip,
  Typography,
  Dialog,
  DialogContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import { FileData } from "../../data/models";
import {
  ClassificationService,
  ClassificationDocument
} from "../../data/services/classification.service";
import { ApiResult } from "common/models";
import "./CategoryDetail.scss";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";

interface CategoryDetailProps {
  category: string;
  fileCount: number;
  batchGuid?: string;
  classificationName?: string;
  onBack: () => void;
  onExtractAll: () => void;
}

const CategoryDetail: React.FC<CategoryDetailProps> = ({
  category,
  fileCount,
  batchGuid,
  classificationName,
  onBack,
  onExtractAll
}) => {
  const [files, setFiles] = useState<FileData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isClassifying, setIsClassifying] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    const fetchClassificationDocuments = async () => {
      setLoading(true);
      setError(null);

      try {
        // batch-specific API - batchGuid is required
        if (!batchGuid) {
          setError("Batch GUID is required for classification documents");
          setFiles([]);
          setLoading(false);
          return;
        }

        //batch-specific API
        const apiParams = { category: category };
        const result =
          await ClassificationService.getClassificationDocumentsByBatch(
            batchGuid,
            apiParams
          );

        if (!result.hasErrors && result.value) {
          const transformedFiles: FileData[] = result.value.map(
            (doc: ClassificationDocument) => ({
              id: doc.guidCode,
              name: doc.fileName,
              category: doc.predictedCategory,
              confidence:
                doc.confidence > 1 ? doc.confidence / 100 : doc.confidence,
              reason: doc.reason,
              validated: false,
              guidCode: doc.guidCode,
              title: doc.title,
              fileName: doc.fileName,
              predictedCategory: doc.predictedCategory,
              summary: doc.summary
            })
          );

          setFiles(transformedFiles);
        } else {
          setFiles([]);
          const errorMessage =
            result.errors?.join(", ") ||
            `Failed to load documents for batch: ${
              classificationName || "Unknown"
            }`;
          setError(errorMessage);
        }
      } catch (err) {
        setFiles([]);
        const errorMessage = `Failed to load documents for batch: ${
          classificationName || "Unknown"
        }. Please try again later.`;
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchClassificationDocuments();
  }, [category, fileCount, batchGuid, classificationName]);

  const handleReclassify = async (fileId: string) => {
    // Reclassify button clicked for file: {fileId}
    setIsClassifying(true);

    // Simulate classification process (2-3 seconds) - just for loading state
    setTimeout(() => {
      // Classification process completed for file: {fileId}
      setIsClassifying(false);
      setShowSuccessPopup(true);

      // Hide success popup after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    }, Math.random() * 1000 + 2000); // 2-3 seconds
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return "success";
    if (confidence >= 0.7) return "warning";
    return "error";
  };

  const getConfidenceClass = (confidence: number) => {
    if (confidence >= 0.9) return "success";
    if (confidence >= 0.7) return "warning";
    return "error";
  };

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="category-detail">
        {/* Header */}
        <HubHeader
          title="Classification Hub"
          description="Classify documents and validate category"
          showBackButton={true}
          onBack={onBack}
        />
        <div className="category-header">
          <div className="header-left">
            <div className="category-info">
              <Typography variant="h4" className="category-title">
                {category}
              </Typography>
              <Typography variant="body1" className="category-count">
                {fileCount} {fileCount === 1 ? "document" : "documents"}
              </Typography>
            </div>
          </div>
          {/* <Button
            variant="contained"
            startIcon={<Download />}
            onClick={onExtractAll}
            className="extract-button"
          >
            Extract All Documents
          </Button> */}
        </div>

        {/* Files Table */}
        <TableContainer component={Paper} className="files-table-container">
          <Table className="files-table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "30%", textAlign: "start" }}>
                  Document
                </TableCell>
                <TableCell style={{ width: "15%", textAlign: "start" }}>
                  Confidence
                </TableCell>
                <TableCell style={{ width: "40%", textAlign: "start" }}>
                  Classification Reason
                </TableCell>
                <TableCell style={{ width: "15%", textAlign: "start" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    <CircularProgress
                      size={24}
                      style={{ marginRight: "0.5rem" }}
                    />
                    Loading documents...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    <Typography
                      variant="body2"
                      color="error"
                      style={{ marginBottom: "0.5rem" }}
                    >
                      {error}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => window.location.reload()}
                      size="small"
                    >
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              ) : files.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      No documents found for category &ldquo;{category}&rdquo;
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                files.map((file) => (
                  <TableRow key={file.id} className="file-row">
                    <TableCell>
                      <div className="file-info-cell">
                        <Description className="file-icon" />
                        <div className="file-details">
                          <Typography variant="body1" className="file-name">
                            {file.fileName || file.name}
                          </Typography>
                          {/* <Typography variant="caption" className="file-type">
                            {(file.fileName || file.name)
                              .split(".")
                              .pop()
                              ?.toUpperCase() || "PDF"}
                          </Typography> */}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="confidence-cell">
                        <Chip
                          size="small"
                          label={`${Math.round(
                            file.confidence > 1
                              ? file.confidence
                              : file.confidence * 100
                          )}%`}
                          color={getConfidenceColor(
                            file.confidence > 1
                              ? file.confidence / 100
                              : file.confidence
                          )}
                          className="confidence-chip"
                        />
                        <div className="confidence-bar">
                          <div
                            className={`confidence-fill ${getConfidenceClass(
                              file.confidence > 1
                                ? file.confidence / 100
                                : file.confidence
                            )}`}
                            style={{
                              width: `${
                                file.confidence > 1
                                  ? file.confidence
                                  : file.confidence * 100
                              }%`
                            }}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" className="reason-text">
                        {file.reason}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {/* {category === "Unknown" ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Refresh />}
                          onClick={() => handleReclassify(file.id)}
                          className="classify-button"
                          disabled={isClassifying}
                        >
                          Classify
                        </Button>
                      ) : ( */}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={() => handleReclassify(file.id)}
                        className="reclassify-button"
                        disabled={isClassifying}
                      >
                        Re-classify
                      </Button>
                      {/* )} */}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Classification Progress Dialog */}
      <Dialog
        open={isClassifying}
        className="classification-progress-dialog"
        PaperProps={{
          style: {
            borderRadius: "12px",
            padding: "1rem",
            background: "white"
          }
        }}
      >
        <DialogContent
          className="progress-content"
          style={{ textAlign: "center", padding: "2rem" }}
        >
          <CircularProgress
            size={48}
            className="progress-spinner"
            style={{ color: "#2563eb", marginBottom: "1rem" }}
          />
          <Typography
            variant="h6"
            className="progress-title"
            style={{
              fontFamily: "EY Interstate, Arial, sans-serif",
              fontWeight: 600,
              color: "#0f172a",
              marginBottom: "0.5rem",
              fontSize: "1.25rem"
            }}
          >
            Classification in Progress
          </Typography>
          <Typography
            variant="body2"
            className="progress-message"
            style={{
              color: "#6b7280",
              fontFamily: "EY Interstate, Arial, sans-serif",
              fontSize: "0.875rem"
            }}
          >
            Please wait while we re-analyze the document...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        className="classification-success-dialog"
        PaperProps={{
          style: {
            borderRadius: "12px",
            padding: "0.5rem",
            background: "white"
          }
        }}
      >
        <DialogContent
          className="success-content"
          style={{ textAlign: "center", padding: "1.5rem" }}
        >
          <CheckCircle
            className="success-icon"
            style={{
              color: "#22c55e",
              fontSize: "3rem",
              marginBottom: "1rem"
            }}
          />
          <Typography
            variant="h6"
            className="success-title"
            style={{
              fontFamily: "EY Interstate, Arial, sans-serif",
              fontWeight: 600,
              color: "#0f172a",
              marginBottom: "0.5rem",
              fontSize: "1.25rem"
            }}
          >
            Re-classification Complete
          </Typography>
          <Typography
            variant="body2"
            className="success-message"
            style={{
              color: "#6b7280",
              fontFamily: "EY Interstate, Arial, sans-serif",
              fontSize: "0.875rem"
            }}
          >
            Document has been re-classified successfully.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CategoryDetail;
